import { GetDistributionResponse } from '@upright/common/model/distributions/api-types';
import { Percentiler } from '@upright/common/model/distributions/percentiler';
import { DistributionClass, DistributionFeature } from '@upright/common/model/distributions/types';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { getVersionAndRelease } from '@upright/ui/untidy/redux/selectors';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

export function useDistributionData(dClass: DistributionClass, feature: DistributionFeature, release?: string | null) {
  const { release: selectedRelease } = useSelector(getVersionAndRelease);
  const releaseToUse = release ?? selectedRelease;

  return useQuery({
    queryKey: ['get-distribution', dClass, feature, releaseToUse],

    queryFn: async () => {
      return await fetchDistributionData(dClass, feature, releaseToUse!);
    },

    enabled: !!releaseToUse,
    // Distributions are immutable per (class, feature, release). `release` is part of the
    // queryKey, so a release bump produces a fresh entry — caching forever within a tab session
    // collapses request count to ~1 per (class, feature, release) instead of ~1 per mount.
    staleTime: Infinity,
  });
}

// Multiple features version. Transform into percentilers early, since sorting the distribution is
// expensive for every render.
export function useDistributionPercentilers(
  dClass: DistributionClass | undefined,
  features: DistributionFeature[],
  release?: string | null,
) {
  const { release: selectedRelease } = useSelector(getVersionAndRelease);
  const releaseToUse = release ?? selectedRelease;

  return useQueries({
    queries: features.map(feature => {
      return {
        queryKey: ['get-distribution', dClass, feature, releaseToUse],

        queryFn: async () => {
          return await fetchDistributionData(dClass, feature, releaseToUse!);
        },

        select: transform,
        enabled: !!releaseToUse && !!dClass,
        // See comment on `useDistributionData` — same rationale (release is in queryKey).
        staleTime: Infinity,
      };
    }),
  });
}

const transform = (response: GetDistributionResponse) => new Percentiler(response);

async function fetchDistributionData(
  dClass: DistributionClass | undefined,
  feature: DistributionFeature,
  release: string,
) {
  return await fetchOrThrow<GetDistributionResponse>(`/distribution/${dClass}/${feature}?release=${release}`);
}
